import {
  APIBlobResponse,
  PaginatedAPIResponse,
  APIResponse,
} from "../models/NetworkResponse";
import { MockupComment } from "../models/Order/MockupComment";
import { OrdersFilters, OrderTypeFilter } from "../models/Order/Order";
import { OrderBasicDetails, OrderStatus } from "../models/Order/OrderDetails";
import { OrderMockup } from "../models/Order/OrderMockup";
import {
  OrderProduct,
  OrderServices,
  PackConfigRequest,
  PackProduct,
} from "../models/Order/OrderProduct";
import { OrderMockupRequest, OrderRequest } from "../models/Order/OrderRequest";
import { OrderActivityLog } from "../models/Order/OrderActivityLog";

import {
  deleteAPI,
  getAPI,
  postAPI,
  postMultipartAPI,
  putAPI,
  downloadPDF,
} from "./Networking";
export default class OrderAPI {
  getOrderIds = async (searchText: string): Promise<number[]> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("search_text", searchText);

      const response = await getAPI(
        `/v5/orders/order-ids?${queryParams.toString()}`,
      );

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  searchOrderIdsAndLabels = async (
    customerId: number,
    searchText?: string,
  ): Promise<{ id: number; label: string }[]> => {
    try {
      const queryParams = new URLSearchParams();
      if (searchText) {
        queryParams.append("search_text", searchText);
      }

      queryParams.append("customer_id", customerId.toString());

      const response = await getAPI(
        `/v5/orders/search?${queryParams.toString()}`,
      );

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getOrderListing = async (
    filters: OrdersFilters,
  ): Promise<PaginatedAPIResponse<OrderBasicDetails>> => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.page_no !== undefined)
        queryParams.append("page_no", filters.page_no.toString());

      if (filters.limit !== undefined)
        queryParams.append("limit", filters.limit.toString());

      if (filters.search_text)
        queryParams.append("search_text", filters.search_text);

      if (filters.sort_by) queryParams.append("sort_by", filters.sort_by);

      if (filters.sort_type) queryParams.append("sort_type", filters.sort_type);

      if (filters.start_date)
        queryParams.append("start_date", filters.start_date.toISOString());

      if (filters.end_date)
        queryParams.append("end_date", filters.end_date.toISOString());

      if (filters.admin_id && Array.isArray(filters.admin_id)) {
        const adminIds = filters.admin_id.map((item) => item.id).join(",");
        queryParams.append("admin_id", adminIds);
      }

      if (filters.user_id && Array.isArray(filters.user_id)) {
        const userIds = filters.user_id.map((item) => item.id).join(",");
        queryParams.append("user_id", userIds);
      }

      if (filters.customer_id && Array.isArray(filters.customer_id)) {
        const customerIds = filters.customer_id
          .map((item) => item.id)
          .join(",");
        queryParams.append("customer_id", customerIds);
      }

      if (filters.order_id && Array.isArray(filters.order_id)) {
        const orderIds = filters.order_id.map((item) => item.id).join(",");
        queryParams.append("order_id", orderIds);
      }

      if (filters.order_type && Array.isArray(filters.order_type)) {
        const selectedValues = filters.order_type.map((item) =>
          item.value.toLowerCase().replace(/\s+/g, "_"),
        );
        OrderTypeFilter.forEach((filter) => {
          const formattedValue = filter.value
            .toLowerCase()
            .replace(/\s+/g, "_");
          if (selectedValues.includes(formattedValue)) {
            queryParams.append(formattedValue, "true");
          }
        });
      }

      if (filters.order_types && Array.isArray(filters.order_types)) {
        const orderTypes = filters.order_types.join(",");
        queryParams.append("order_types", orderTypes);
      }

      const response = await getAPI(`/v5/orders?${queryParams.toString()}`);

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getOrderDetailsById = async (orderId: number): Promise<OrderBasicDetails> => {
    try {
      const response = await getAPI(`/v5/orders/order-id/${orderId}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  deleteProduct = async (
    productId: number,
    orderId: number,
  ): Promise<OrderRequest> => {
    try {
      const body = {
        order_id: orderId,
        product_id: productId,
      };
      const response = await deleteAPI(`/v5/orders/product`, body);

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateProductQuantity = async (
    orderId: number,
    productId: number,
    updatedQuantity: number,
  ): Promise<OrderRequest> => {
    try {
      const body = {
        product_id: productId,
        order_id: orderId,
        quantity: updatedQuantity,
      };
      const response = await putAPI(`/v5/orders/update-product-quantity`, body);

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateProductSellingPrice = async (
    orderId: number,
    productId: number,
    updatedQuantity: number,
  ): Promise<OrderRequest> => {
    try {
      const body = {
        product_id: productId,
        order_id: orderId,
        selling_price: updatedQuantity,
      };
      const response = await putAPI(`/v5/orders/update-selling-price`, body);

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateProductDiscount = async (
    orderId: number,
    productId: number,
    updatedQuantity: number,
  ): Promise<OrderRequest> => {
    try {
      const body = {
        product_id: productId,
        order_id: orderId,
        discount_percentage: updatedQuantity,
      };
      const response = await putAPI(`/v5/orders/add-discount`, body);

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateProductOfferPrice = async (
    orderId: number,
    productId: number,
    updatedQuantity: number,
  ): Promise<OrderRequest> => {
    try {
      const body = {
        product_id: productId,
        order_id: orderId,
        unit_price: updatedQuantity,
      };
      const response = await putAPI(`/v5/orders/update-unit-price`, body);

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateProductHsn = async (
    orderId: number,
    productId: number,
    updatedQuantity: number,
    pr_id?: number,
  ): Promise<OrderRequest> => {
    try {
      let url = `/v5/products/product-hsn`;
      if (pr_id) {
        url += `?pr_id=${pr_id}`;
      }
      const body = {
        id: productId,
        order_id: orderId,
        hsn_code: updatedQuantity,
      };
      const response = await putAPI(url, body);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateProductGst = async (
    orderId: number,
    productId: number,
    updatedQuantity: number,
  ): Promise<OrderRequest> => {
    try {
      const body = {
        product_id: productId,
        order_id: orderId,
        gst_percentage: updatedQuantity,
      };
      const response = await putAPI(`/v5/orders/update-product-gst`, body);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  addProducts = async (
    products: OrderProduct[],
    orderId: number,
  ): Promise<OrderRequest> => {
    try {
      const body = {
        products: products,
        order_id: orderId,
      };
      const response = await putAPI(`/v5/orders/add-products`, body);

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  deleteService = async (
    serviceId: number,
    custom_name: string,
    orderId: number,
  ): Promise<OrderRequest> => {
    try {
      const body = {
        order_id: orderId,
        service_id: serviceId,
        custom_name,
      };
      const response = await deleteAPI(`/v5/orders/service`, body);

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateServiceQuantity = async (
    serviceId: number,
    productId: number,
    custom_name: string,
    updatedQuantity: number,
  ): Promise<OrderRequest> => {
    try {
      const body = {
        service_id: productId,
        order_id: serviceId,
        quantity: updatedQuantity,
        custom_name: custom_name,
      };
      const response = await putAPI(`/v5/orders/service/update-quantity`, body);

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateServiceSellingPrice = async (
    orderId: number,
    serviceId: number,
    custom_name: string,
    updatedQuantity: number,
  ): Promise<OrderRequest> => {
    try {
      const body = {
        service_id: serviceId,
        order_id: orderId,
        selling_price: updatedQuantity,
        custom_name: custom_name,
      };
      const response = await putAPI(
        `/v5/orders/service/update-selling-price`,
        body,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateServiceOfferPrice = async (
    orderId: number,
    serviceId: number,
    custom_name: string,
    updatedQuantity: number,
  ): Promise<OrderRequest> => {
    try {
      const body = {
        service_id: serviceId,
        order_id: orderId,
        unit_price: updatedQuantity,
        custom_name: custom_name,
      };
      const response = await putAPI(
        `/v5/orders/service/update-unit-price`,
        body,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  addServices = async (
    services: OrderServices[],
    orderId: number,
  ): Promise<OrderRequest> => {
    try {
      const body = {
        services: services,
        order_id: orderId,
      };
      const response = await putAPI(`/v5/orders/add-services`, body);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  createOrder = async (
    orderRequest: OrderRequest,
  ): Promise<OrderBasicDetails> => {
    try {
      const body = orderRequest;
      const response = await postAPI(`/v5/orders`, body);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateOrder = async (
    orderRequest: OrderRequest,
  ): Promise<OrderBasicDetails> => {
    try {
      const body = orderRequest;
      const response = await putAPI(`/v5/orders/update-details`, body);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateClientPODetails = async (
    orderId: number,
    clientPONumber: string,
    clientPOFileUrl: string,
  ): Promise<OrderBasicDetails> => {
    try {
      const body = {
        order_id: orderId,
        client_po_number: clientPONumber,
        client_po_file_url: clientPOFileUrl,
      };
      const response = await putAPI(`/v5/orders/client-po-details`, body);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateOrderDiscount = async (
    orderId: number,
    discount: number,
  ): Promise<OrderBasicDetails> => {
    try {
      const body = {
        order_id: orderId,
        discount_percentage: discount,
      };
      const response = await putAPI(`/v5/orders/add-discount`, body);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  addExchangeRate = async (
    orderId: number,
    exchangeRate: number,
    currency: string,
  ): Promise<OrderBasicDetails> => {
    try {
      const body = {
        order_id: orderId,
        currency_code: currency,
        exchange_rate: exchangeRate,
      };
      const response = await putAPI(
        `/v5/orders/order-id/${orderId}/exchange-rate`,
        body,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  recalculateOrder = async (orderId: number): Promise<OrderBasicDetails> => {
    try {
      const response = await putAPI(
        `/v5/orders/order-id/${orderId}/recalculate`,
        {},
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  duplicateOrder = async (orderId: number): Promise<OrderBasicDetails> => {
    try {
      const body = {
        order_id: orderId,
      };
      const response = await postAPI(`/v5/orders/duplicate`, body);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  sendTaxInvoice = async (orderId: number): Promise<OrderBasicDetails> => {
    try {
      const response = await getAPI(
        `/v5/orders/${orderId}/invoices/send-email-notification`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  generatePaymentLink = async (
    orderId: number,
    providerId: string,
  ): Promise<APIResponse> => {
    try {
      const response = await getAPI(
        `/v5/orders/${orderId}/payment-link/generate/provider/${providerId}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  downloadPerformaInvoice = async (
    orderId: number,
  ): Promise<APIBlobResponse> => {
    try {
      const response = await downloadPDF(
        `/v5/orders/${orderId}/proforma-invoice`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };
  downloadPAndL = async (orderId: number) => {
    try {
      const response = await downloadPDF(
        `/admin/orders/orderId/${orderId}/P&L`,
      );
      return response as Blob;
    } catch (error) {
      throw error;
    }
  };

  deleteorder = async (orderId: number) => {
    try {
      const response = await deleteAPI(`/v5/orders/order-id/${orderId}`);

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  fetchMockups = async (
    orderId: number,
    productId: number,
  ): Promise<OrderMockup[]> => {
    try {
      const response = await getAPI(
        `/v5/mockups/order/${orderId}/product/${productId}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  uploadMockupForProduct = async (
    productMockup: OrderMockupRequest,
    orderId: number,
  ) => {
    try {
      const response = await postAPI(`/v5/mockups/`, {
        mockups: productMockup,
        order_id: orderId,
      });
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  generateOrderMockup = async (payload: {
    order_id: number;
    product_id: number;
    psd_template_id: number;
    logo_url: string;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation?: number;
    mockup_id?: number;
  }) => {
    try {
      const response = await postAPI(`/v5/mockups/generate-mockup`, payload);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  fetchMockupComments = async (mockupId: number): Promise<MockupComment[]> => {
    try {
      const response = await getAPI(`/v5/mockups/mockup/${mockupId}/comments`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  postMockupComment = async (mockupComment: MockupComment) => {
    try {
      const response = await postAPI(`/v5/mockups/comment`, mockupComment);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  deleteMockup = async (mockup_id: number, order_id: number) => {
    try {
      const response = await deleteAPI(`/v5/mockups/`, { mockup_id, order_id });
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateAllQuantities = async (
    orderId: number,
    quantity: number,
  ): Promise<OrderRequest> => {
    try {
      let body = {
        order_id: orderId,
        quantity: quantity,
      };
      const response = await putAPI(
        `/v5/orders/all/update-product-quantity/`,
        body,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getOrderBasicDetails = async (
    orderId: number,
  ): Promise<OrderBasicDetails> => {
    try {
      const response = await getAPI(
        `/v5/orders/order-id/${orderId}/basic-details`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateStatus = async (
    status: OrderStatus,
    orderId: number,
  ): Promise<OrderBasicDetails> => {
    try {
      const body = {
        status: status,
        order_id: orderId,
      };
      const response = await putAPI(`/v5/orders/update-status`, body);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateSezStatus = async (
    orderId: number,
    is_sez: number,
  ): Promise<OrderBasicDetails> => {
    try {
      const body = {
        order_id: orderId,
        is_sez: is_sez,
      };
      const response = await putAPI(`/v5/orders/sez`, body);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateInternationalOrderStatus = async (
    orderId: number,
    is_international_order: number,
  ): Promise<OrderBasicDetails> => {
    try {
      const body = {
        order_id: orderId,
        is_international_order: is_international_order,
      };
      const response = await putAPI(`/v5/orders/international-order`, body);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateMonthlyInvoiceStatus = async (
    orderId: number,
    is_monthly_invoice: number,
  ): Promise<OrderBasicDetails> => {
    try {
      const body = {
        order_id: orderId,
        is_monthly_invoice: is_monthly_invoice,
      };
      const response = await putAPI(`/v5/orders/monthly-invoice`, body);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  addPacks = async (
    products: PackProduct[],
    label: string,
    quantity: number,
    orderId: number,
  ): Promise<any> => {
    try {
      const body: PackConfigRequest = {
        products,
        label,
        quantity,
        order_id: orderId,
      };
      const response = await postAPI(
        `/v5/orders/${orderId}/pack-configuration`,
        body,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };
  getPacks = async (orderId: number): Promise<any> => {
    try {
      const response = await getAPI(
        `/v5/orders/${orderId}/pack-configurations`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  deletePack = async (pack_id: number, orderId: number): Promise<any> => {
    try {
      const body = {
        order_id: orderId,
        pack_id: pack_id,
      };
      const response = await deleteAPI(
        `/v5/orders/${orderId}/pack-configuration/${pack_id}`,
        body,
      );

      return response.result;
    } catch (error) {
      throw error;
    }
  };
  // updatePacks = async (
  //   products: PackProduct[],
  //   label: string,
  //   quantity: number,
  //   orderId: number,
  //   packConfigId: number
  // ): Promise<any> => {
  //   try {
  //     const body: PackConfigRequest = {
  //       products,
  //       label,
  //       quantity,
  //       order_id: orderId
  //     };
  //     const response = await putAPI(
  //       `/v5/orders/${orderId}/pack-configuration/${packConfigId}`,
  //       body
  //     );
  //     return response.result;
  //   } catch (error) {
  //     throw error;
  //   }
  // };

  addOrderLogo = async (
    orderId: number,
    productId: number,
    files: File[],
  ): Promise<APIResponse> => {
    try {
      if (!files || files.length === 0) {
        throw new Error("At least one file is required.");
      }

      if (files.length > 10) {
        throw new Error("You can upload up to 10 files at once.");
      }

      if (!productId) {
        throw new Error("product_id is required.");
      }

      const formData = new FormData();
      formData.append("product_id", String(productId));
      files.forEach((file) => formData.append("images", file));

      const response = await postMultipartAPI(
        `/v5/orders/order/${orderId}/artWork`,
        formData,
      );

      const normalizedResult = Array.isArray(response.result)
        ? response.result
        : Array.isArray(response.result?.images)
          ? response.result.images
          : Array.isArray(response.result?.urls)
            ? response.result.urls
            : [];

      return {
        ...response,
        result: normalizedResult,
      };
    } catch (error) {
      throw error;
    }
  };

  getAllOrderArtworks = async (orderId: number): Promise<APIResponse> => {
    try {
      const response = await getAPI(
        `/v5/orders/order/${orderId}/getAllOrderArtWorks`,
      );
      return response;

      // return response.result;
    } catch (error) {
      throw error;
    }
  };

  getSingleOrderArtwork = async (orderId: number): Promise<APIResponse> => {
    try {
      const response = await getAPI(`/v5/orders/order/${orderId}/artworks`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  uploadPackImage = async (
    orderId: number,
    packId: number,
    imageUrl: string,
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI(
        `/v5/orders/${orderId}/pack-configuration/${packId}/image`,
        { image_url: imageUrl },
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  approveMockup = async (
    mockup_id: number,
    order_id: number,
    approval_note?: string,
    imageUrls?: string[],
  ) => {
    try {
      const response = await postAPI(`/v5/mockups/approve-mockup`, {
        mockup_id,
        order_id,
        approval_note: approval_note || "",
        images: imageUrls || [],
      });
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  rejectMockup = async (
    mockup_id: number,
    order_id: number,
    rejection_note?: string,
    imageUrls?: string[],
  ) => {
    try {
      const response = await postAPI(`/v5/mockups/reject-mockup`, {
        mockup_id,
        order_id,
        rejection_note: rejection_note || "",
        images: imageUrls || [],
      });
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getOrderActivityLogs = async (
    orderId: number,
  ): Promise<OrderActivityLog[]> => {
    try {
      const response = await getAPI(`/v5/orders/${orderId}/activity-logs`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getProposalStats = async (orderId: number): Promise<APIResponse> => {
    try {
      const response = await getAPI(
        `/v5/orders/order-id/${orderId}/proposal-stats`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getProductTatInsights = async (orderId: number): Promise<APIResponse> => {
    try {
      const response = await getAPI(
        `/v5/orders/order-id/${orderId}/product-tat-insights`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };
}
